import type {
  CpuAimDisplay,
  GameState,
  Intent,
  MatchConfig,
  MatchMode,
  SimEvent,
  SpeciesId,
  ThemeId,
  WeaponId,
} from './types.js';
import { PROTOCOL_VERSION } from './constants.js';

export { PROTOCOL_VERSION };

export type RoomErrorCode =
  | 'FULL'
  | 'NOT_FOUND'
  | 'VERSION'
  | 'IN_PROGRESS'
  | 'BAD_INPUT'
  | 'NAME_TAKEN'
  /** Instant match is switched off in the server's config file. */
  | 'MM_DISABLED'
  /** The instant-match queue is at its configured cap. */
  | 'QUEUE_FULL'
  /** The host asked for an invitation code that is already in use. */
  | 'CODE_TAKEN';

export interface RoomPlayer {
  playerId: string;
  name: string;
  ready: boolean;
  seat: number | null;
  /** Squad picks (up to config.teamSize) chosen in the menu. */
  squad?: SpeciesId[];
}

export interface RoomStateMsg {
  code: string;
  hostId: string;
  players: RoomPlayer[];
  config: PublicMatchConfig;
  inProgress: boolean;
  /** `code` = invite room, `instant` = a matchmade game from the server config. */
  kind: 'code' | 'instant';
  /** Instant rooms: seconds until the match begins (present while waiting). */
  countdownS?: number;
  /** Instant rooms: the seat-by-seat roster, humans and named bots (while waiting). */
  roster?: { name: string; human: boolean }[];
}

/** MatchConfig as shared over the wire (seed revealed only at match_start). */
export type PublicMatchConfig = Omit<MatchConfig, 'seed'>;

export interface MatchStartMsg {
  seed: number;
  config: MatchConfig;
  roster: { seat: number; playerId: string | null; name: string; species?: SpeciesId[] }[];
  yourPlayerId: string;
}

/**
 * GameState without terrain pixels, carve log or event queue. `cpuAim` ships
 * display-only: the pending AI intent rides in GameState server-side but must
 * never reach a client.
 */
export type SnapshotMsg = Omit<
  GameState,
  'terrain' | 'carveLog' | 'events' | 'rng' | 'cpuAim'
> & {
  rngState: number;
  cpuAim: CpuAimDisplay | null;
};

/**
 * The instant-match queue, as advertised to a waiting searcher.
 *
 * A matchmade game is humans only: the queue waits for `needed` people and
 * seats every one of them, so no seat is ever filled by a CPU — never
 * something the queue does behind a player's back.
 */
export interface MatchmakingMsg {
  /** Total seats in the instant match. */
  seats: number;
  /** Humans currently waiting (including you). */
  queued: number;
  /** Whether *this* recipient is in the queue (false for a bystander's status push). */
  youQueued: boolean;
  /** How many more humans the queue is waiting for before it starts a match. */
  needed: number;
  /** Non-null only after the server folded the humans into the roster: one slot per seat. */
  slots: { name: string; human: boolean }[] | null;
  /** Whether the result screen offers a one-tap rematch (a fresh search). */
  rematch: boolean;
}

export interface MatchEndMsg {
  winnerTeamId: number | null;
  stats: { teamId: number; name: string; damage: number; kills: number; alive: boolean }[];
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

export type ClientMessage =
  | { t: 'hello'; d: { protocolVersion: number; name: string } }
  | {
      t: 'create_room';
      d: {
        name: string;
        config: PublicMatchConfig;
        squad?: SpeciesId[];
        /** Host-chosen invitation code; omitted = the server picks one. */
        code?: string;
      };
    }
  | { t: 'join_room'; d: { code: string; name: string; token?: string; squad?: SpeciesId[] } }
  | { t: 'find_match'; d: { name: string; token?: string; squad?: SpeciesId[] } }
  | { t: 'cancel_match' }
  | { t: 'leave_room' }
  | { t: 'set_ready'; d: { ready: boolean } }
  | { t: 'set_config'; d: { config: Partial<PublicMatchConfig> } }
  | { t: 'submit_intent'; d: { intent: Intent } }
  /**
   * Live aim while our seat drives the active character: the pointer's WORLD
   * position (what the local `aim` render source is) plus the weapon in hand.
   * Throttled (~50 ms) from the pointermove hook; display only — the server
   * relays it to the other seats and never feeds it to the sim.
   */
  | { t: 'aim_state'; d: { x: number; y: number; weaponId: WeaponId | null } }
  | { t: 'request_state' }
  | { t: 'ping'; d: { t: number } };

export type ServerMessage =
  | { t: 'welcome'; d: { playerId: string; protocolVersion: number } }
  | { t: 'room_state'; d: RoomStateMsg }
  | { t: 'room_error'; d: { code: RoomErrorCode; msg: string } }
  | { t: 'match_start'; d: MatchStartMsg }
  | { t: 'snapshot'; d: SnapshotMsg }
  | { t: 'sim_event'; d: { events: SimEvent[] } }
  /**
   * Lockstep input relay: a human intent the server applied, stamped with the
   * exact sim tick it was applied at. Every client (sender included) queues
   * it and applies it locally at that same tick — identical seed + identical
   * input sequence = identical world everywhere (the Worms model). CPU
   * decisions are never relayed: every machine derives them deterministically
   * at the same tick.
   */
  | { t: 'intent_applied'; d: { intent: Intent; tick: number } }
  | { t: 'intent_ack'; d: { ok: boolean; reason?: string; turnIndex?: number } }
  /**
   * Another seat's live aim, relayed with the sender's playerId stamped by
   * the server (never the client's word). Sent to everyone but the sender.
   */
  | {
      t: 'aim_state';
      d: { playerId: string; x: number; y: number; weaponId: WeaponId | null };
    }
  | {
      t: 'player_event';
      d: {
        kind: 'joined' | 'left' | 'disconnected' | 'reconnected';
        playerId: string;
        name: string;
        /** On a mid-match 'disconnected': seconds until the match is forfeited. */
        graceS?: number;
      };
    }
  | { t: 'matchmaking'; d: MatchmakingMsg }
  | { t: 'match_end'; d: MatchEndMsg }
  /**
   * Echo of the client's `ping` timestamp (for RTT) plus the server's current
   * sim tick of that player's room (0 outside a match) — the clock beacon the
   * lockstep peer steers its tick counter against.
   */
  | { t: 'pong'; d: { t: number; tick: number } };

export function encode<T extends ClientMessage | ServerMessage>(msg: T): string {
  return JSON.stringify({ v: PROTOCOL_VERSION, ...msg });
}

export function decodeClient(raw: string): ClientMessage | null {
  return parse(raw) as ClientMessage | null;
}

export function decodeServer(raw: string): ServerMessage | null {
  return parse(raw) as ServerMessage | null;
}

function parse(raw: string): { t: string; d: unknown } | null {
  try {
    const obj = JSON.parse(raw) as { v?: number; t?: unknown; d?: unknown };
    if (typeof obj !== 'object' || obj === null) return null;
    if (obj.v !== PROTOCOL_VERSION) return null;
    if (typeof obj.t !== 'string') return null;
    return { t: obj.t, d: obj.d };
  } catch {
    return null;
  }
}

export const DEFAULT_CONFIG: PublicMatchConfig = {
  theme: 'meadow',
  mode: 'teams',
  teamSize: 3,
  seats: 2,
  difficulty: 'normal',
  turnTimeMs: 30_000,
};

export type { MatchMode, ThemeId };
